import { ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { CompSearchState, PrimaryFunctions } from '@xbs/xbs-enums';
import { AnalysisSideMenuProps, csRunningStatus } from './AnalysisSideMenu.proptype';
import { csRunningStates } from '../../components/CompSearchAnimation/CompSearchAnimation.proptype';
import { FailedCsStates, FetchLoadingStateEnum } from '../../constants';
import { Country } from '../../models';
import {
  fetchCompSearchStatus,
  fetchJurisdictionDescription,
  fetchPbaJurisdictions,
  fetchPBAPli,
  doneCSRunning,
  removeCurrentPbaJurisdictions,
  clearPbaData
} from '../../redux/profitBasedAnalyses';
import { clearTestedPartyData, fetchCurrentDistributor, fetchSICTypeOptions } from '../../redux/testedParty';
import { fetchTransactions } from '../../redux/transactions';
import {
  selectCurrentTestedParty,
  selectTransactionByPbaId,
  selectPBAPli,
  selectPbaJurisdictionCountry,
  selectCurrentPBA,
  selectcsStatus,
  selectPbaJurisdictions,
  selectCurrentMajorClassification,
  selectCurrentValueAddedDistributor,
  selectCSJurisdictionRunId,
  selectPbaJurisdictionInfo,
  selectPBALoadingState
} from '../../selectors';
import { AppDispatch } from '../../store';
import { PliValues } from '../ProfitLevelIndicator/ProfitLevelIndicator.proptype';
import { MajorClassification } from '../TestedPartyCharacterization';
import { CharacterizationTypeEnum, Distributor } from '../TestedPartyDistributor';

const Connector = ({ component: Component }: { component: ComponentType<AnalysisSideMenuProps> }) => {
  const [currentCSStatus, setCurrentCSStatus] = useState<csRunningStatus>(csRunningStates.notRunning);
  const csJurisdictionRunId = useSelector(selectCSJurisdictionRunId);
  const pbaJurisdictionDetails = useSelector(selectPbaJurisdictions);
  const currentMajorClassifications: MajorClassification[] = useSelector(selectCurrentMajorClassification);
  const currentValueAddedDistributor: Distributor = useSelector(selectCurrentValueAddedDistributor);
  const pbaJurisdictions: Country[] = useSelector(selectPbaJurisdictionCountry);
  const pba = useSelector(selectCurrentPBA);
  const match: any = useRouteMatch('/analysis/:studyId/pba-dashboard/:pbaId');
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const { studyId } = match.params;
  const { pbaId } = match.params;
  const csRunningStatus = useSelector(selectcsStatus);
  const testedParty = useSelector(selectCurrentTestedParty);
  const transactionsPBA = useSelector(selectTransactionByPbaId(Number(pbaId)));
  const pbaPli = useSelector(selectPBAPli);
  const pbaJurisdictionInfo = useSelector(selectPbaJurisdictionInfo(csJurisdictionRunId));
  const loadingStates = useSelector(selectPBALoadingState);

  const navigateToAnalysis = () => {
    if (studyId && pbaId) {
      const path = `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/jurisdiction/${String(
        csJurisdictionRunId
      )}/jurisdiction-analysis-info`;
      history.push(path);
    }
  };

  const isDetailsComplete = Boolean(
    testedParty?.name &&
      testedParty?.justification &&
      testedParty?.segmentType &&
      (testedParty?.primaryFunction.name || testedParty?.otherPrimaryFunction) &&
      transactionsPBA &&
      transactionsPBA.length > 0 &&
      pbaPli?.pliValues.some((val: PliValues) => val.pliValue)
  );

  const assessCharacterization = () => {
    if (PrimaryFunctions.ByType.ServiceProvider.includes(testedParty?.primaryFunction?.primaryFunctionId)) {
      return (
        currentMajorClassifications.length > 0 &&
        currentMajorClassifications.every((major) => major.minorServiceClassifications.length)
      );
    }

    return Boolean(currentValueAddedDistributor.codeType && currentValueAddedDistributor.industrialCodeValue);
  };

  const isCharacterizationComplete = testedParty ? assessCharacterization() : false;
  const searchList = pbaJurisdictions.map((jurisdiction) => {
    const jurisdictionStatus = pba?.jurisdictionStatuses?.find(
      (status) => status.jurisdictionId === jurisdiction?.countryId
    );
    return {
      path: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/jurisdiction/${
        jurisdiction?.countryId
      }/jurisdiction-analysis-info`,
      pathMatch: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/jurisdiction/${jurisdiction?.countryId}`,
      text: jurisdiction?.name,
      disabled: false,
      showProgressIcon: true,
      isComplete: true,
      status: jurisdictionStatus?.status
    };
  });

  useEffect(() => {
    void dispatch(fetchTransactions());
    void dispatch(fetchPBAPli(pbaId));
    void dispatch(fetchPbaJurisdictions(pbaId));
    void dispatch(fetchCurrentDistributor(pbaId));
    void dispatch(fetchSICTypeOptions(CharacterizationTypeEnum.TypeOfOption));

    return () => {
      void dispatch(clearPbaData());
      void dispatch(clearTestedPartyData());
    };
  }, [dispatch, pbaId]);

  useEffect(() => {
    if (pbaJurisdictionDetails && pbaJurisdictionDetails.length > 0) {
      for (const pbaJurisdictionDetail of pbaJurisdictionDetails) {
        if (
          !pbaJurisdictionDetail.pbaJurisdictionInfo &&
          loadingStates.fetchPBAJurisdictionDescription &&
          loadingStates.fetchPBAJurisdictionDescription[pbaJurisdictionDetail.jurisdictionId] !==
            FetchLoadingStateEnum.loading
        ) {
          void dispatch(
            fetchJurisdictionDescription({
              compSearchId: pbaJurisdictionDetail.compSearchId,
              jurisdictionId: pbaJurisdictionDetail.jurisdictionId
            })
          );
        }
      }
    }
  }, [dispatch, loadingStates.fetchPBAJurisdictionDescription, pbaJurisdictionDetails]);

  useEffect(() => {
    let checkingStatus: NodeJS.Timeout;

    if (csRunningStatus === CompSearchState.ByName.Running.Id) {
      setCurrentCSStatus(csRunningStates.running);
      checkingStatus = setInterval(() => {
        void dispatch(fetchCompSearchStatus(pbaId));
      }, 2000);
    }

    if (csRunningStatus === CompSearchState.ByName.Successful.Id) {
      if (pbaJurisdictionDetails && pbaJurisdictionDetails.length > 0) {
        for (const pbaJurisdictionDetail of pbaJurisdictionDetails) {
          if (
            !pbaJurisdictionDetail.pbaJurisdictionInfo &&
            loadingStates.fetchPBAJurisdictionDescription &&
            loadingStates.fetchPBAJurisdictionDescription[pbaJurisdictionDetail.jurisdictionId] !==
              FetchLoadingStateEnum.loading
          ) {
            void dispatch(
              fetchJurisdictionDescription({
                compSearchId: pbaJurisdictionDetail.compSearchId,
                jurisdictionId: pbaJurisdictionDetail.jurisdictionId
              })
            );
          }
        }
      } else {
        void dispatch(fetchPbaJurisdictions(pbaId));
      }

      if (
        csJurisdictionRunId &&
        loadingStates.fetchPBAJurisdictionDescription &&
        loadingStates.fetchPBAJurisdictionDescription[csJurisdictionRunId] === FetchLoadingStateEnum.fulfilled
      ) {
        setCurrentCSStatus(csRunningStates.completed);
        void dispatch(doneCSRunning());
      }
    }

    if (FailedCsStates.includes(csRunningStatus)) {
      setCurrentCSStatus(csRunningStates.failed);
      void dispatch(doneCSRunning());
      void dispatch(removeCurrentPbaJurisdictions());
    }

    return () => {
      clearInterval(checkingStatus);
    };
  }, [
    dispatch,
    csRunningStatus,
    pbaJurisdictionDetails,
    pbaJurisdictionInfo,
    csJurisdictionRunId,
    loadingStates.fetchPBAJurisdictionDescription,
    pbaId
  ]);

  return (
    <Component
      currentCSStatus={currentCSStatus}
      selectedPath={pathname}
      setCurrentCSStatus={setCurrentCSStatus}
      pbaJurisdictionInfo={pbaJurisdictionInfo}
      navigateToAnalysis={navigateToAnalysis}
      sections={[
        {
          title: t('analysis:title-section-tested-party-setup'),
          items: [
            {
              path: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/tested-party-details`,
              text: t('analysis:title-section-item-tested-party-details'),
              disabled: false,
              showProgressIcon: true,
              isComplete: isDetailsComplete
            },
            {
              path: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/tested-party-characterization`,
              text: t('analysis:title-section-item-tested-party-characterization'),
              disabled: false,
              showProgressIcon: true,
              isComplete: isCharacterizationComplete
            },
            {
              path: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/functional-analysis`,
              text: t('analysis:title-section-item-tested-functional-analysis'),
              disabled: false,
              showProgressIcon: false,
              isComplete: false
            }
          ]
        },
        {
          title: t('analysis:title-section-comparable-search'),
          items: [
            {
              path: `/analysis/${String(studyId)}/pba-dashboard/${String(pbaId)}/initial-search`,
              text: t('analysis:title-section-comparable-initial-search'),
              disabled: transactionsPBA?.length === 0,
              isButton: true,
              showProgressIcon: false,
              isComplete: false
            }
          ]
        },
        {
          title: t('analysis:title-section-search-results'),
          items: pbaJurisdictions.length > 0 ? searchList : []
        }
      ]}
      onNavigate={(path) => {
        history.push(`${path}`);
      }}
    />
  );
};

export default Connector;
